import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet } from 'react-native';
import { ScrollView } from 'react-native-gesture-handler';

import { useNavigation } from '@react-navigation/native';

import { AppTrackingAdvertisingSwitch } from '@/components/AppTrackingAdvertisingSwitch';
import { BiometricsSwitch } from '@/components/BiometricSwitch';
import { Button } from '@/components/ButtonComponent';
import { Screen } from '@/components/ScreenComponent';
import { ScreenNavBar } from '@/components/ScreenNavBar';
import { SizedBox } from '@/components/SizedBox';
import { ds } from '@/components/lib/design.system';
import { ActionButton } from '@/feature/account-details/components/ActionButton';
import { UsernameAndAvatarRow } from '@/feature/account-details/components/UsernameAndAvatarRow';
import { useAlerts } from '@/feature/alerts/hooks/use-alerts';
import BetrAnalytics from '@/feature/analytics/analytics';
import { AnalyticsEvent } from '@/feature/analytics/constants';
import { MaxWidthWrapper } from '@/feature/responsive-design/WebComponents';
import { avatarUrlSelector, useAuthUserConfig, usernameSelector } from '@/hooks/use-auth-user-config';
import { mapDefaultVerticalToProduct, useAuthUserSettings } from '@/hooks/use-auth-user-settings';
import { Product } from '@/hooks/use-jurisdiction';
import { useSocialWalletTranslation } from '@/hooks/use-social-wallet-translation';
import { useUserStore } from '@/hooks/use-user-store';
import { useCoreNavigation } from '@/navigation/navigation';
import { AccountTabNavigationProp } from '@/navigation/types';
import { isWeb } from '@/utils/constants-platform-specific';
import { getAppVersion } from '@/utils/get-app-version';
import { maskPhoneNumber } from '@/utils/mask-phone-number';

import { AccountDetailsRow } from '../components/AccountDetailsRow';
import { useCloseAccount } from '../hooks/use-close-account';

export const AccountDetailsScreen = () => {
    const { t } = useTranslation(['account', 'common']);
    const { t: tProduct } = useTranslation('product_switch_menu');
    const tSocialWallet = useSocialWalletTranslation('account');
    const profile = useUserStore(state => state.profile);
    const actions = useUserStore(state => state.actions);
    const { mutate: closeAccount } = useCloseAccount();
    const { showInfoSheet } = useAlerts();
    const navigation = useNavigation<AccountTabNavigationProp>();
    const { openLaunch } = useCoreNavigation();
    const { data: avatarUrl } = useAuthUserConfig({
        select: avatarUrlSelector,
    });
    const { data: username } = useAuthUserConfig({
        select: usernameSelector,
    });
    const { data: userSettings } = useAuthUserSettings();
    const defaultProduct = userSettings?.default_vertical
        ? mapDefaultVerticalToProduct(userSettings.default_vertical)
        : undefined;

    const onCloseAccount = useCallback(() => {
        closeAccount(undefined, {
            onSuccess: () => {
                BetrAnalytics.trackProductEvent(AnalyticsEvent.DETAILS_CLOSEACCT_CONFIRM);
                actions.logout();
                openLaunch();
            },
        });
    }, [closeAccount, actions, openLaunch]);

    const handleOnConfirm = () => {
        showInfoSheet({
            title: t('confirmation_title'),
            description: tSocialWallet('close_account_subtext'),
            buttonLabel: t('close_account'),
            handlePress: onCloseAccount,
            onDismiss: () => BetrAnalytics.trackProductEvent(AnalyticsEvent.DETAILS_CLOSEACCT_CANCEL),
            errorStyling: true,
            showCancel: true,
        });
        BetrAnalytics.trackProductEvent(AnalyticsEvent.DETAILS_CLOSEACCT);
    };

    return (
        <Screen>
            <ScreenNavBar title={t('account_details')} />
            <ScrollView>
                <MaxWidthWrapper paddingHorizontal={'s16'}>
                    <UsernameAndAvatarRow avatarUrl={avatarUrl} username={username} />
                    <AccountDetailsRow
                        label={t('email_address')}
                        subLabel={profile.email}
                        renderAction={() => (
                            <ActionButton
                                onPress={() => {
                                    navigation.navigate('AccountEditStack', { screen: 'EmailUpdate' });
                                    BetrAnalytics.trackProductEvent(AnalyticsEvent.DETAILS_EDIT_EMAIL);
                                }}
                                buttonText={t('common:edit')}
                            />
                        )}
                    />

                    <AccountDetailsRow
                        label={t('phone_number')}
                        subLabel={maskPhoneNumber(profile.mobile_number)}
                        renderAction={() => (
                            <ActionButton
                                onPress={() => {
                                    navigation.navigate('AccountEditStack', {
                                        screen: 'PhoneNumberEditInitialVerification',
                                    });
                                    BetrAnalytics.trackProductEvent(AnalyticsEvent.DETAILS_EDIT_PHONE);
                                }}
                                buttonText={t('common:edit')}
                            />
                        )}
                    />

                    <AccountDetailsRow
                        label={t('common:password')}
                        subLabel={'********'}
                        renderAction={() => (
                            <ActionButton
                                onPress={() => {
                                    navigation.navigate('AccountEditStack', {
                                        screen: 'PhoneVerification',
                                        params: { redirectRoute: 'passwordEdit' },
                                    });
                                    BetrAnalytics.trackProductEvent(AnalyticsEvent.DETAILS_EDIT_PASSWORD);
                                }}
                                buttonText={t('common:edit')}
                            />
                        )}
                    />
                    {isWeb ? null : (
                        <AccountDetailsRow
                            label={t('re_authentication_preferences')}
                            subLabel={t('biometrics')}
                            renderAction={() => <BiometricsSwitch />}
                        />
                    )}

                    <AccountDetailsRow
                        label={t('data_sharing_preferences')}
                        subLabel={t(`targeted_advertising${isWeb ? '_web' : ''}`)}
                        renderAction={() =>
                            isWeb ? (
                                <ActionButton
                                    onPress={() => window.OneTrust?.ToggleInfoDisplay()}
                                    buttonText={t('common:edit')}
                                />
                            ) : (
                                <AppTrackingAdvertisingSwitch />
                            )
                        }
                    />
                    <AccountDetailsRow
                        label={t('common:default_product')}
                        subLabel={
                            defaultProduct && defaultProduct !== Product.None
                                ? tProduct(defaultProduct as Exclude<Product, Product.None>)
                                : undefined
                        }
                        renderAction={() => (
                            <ActionButton
                                onPress={() => navigation.navigate('DefaultProductSelect')}
                                buttonText={t('common:edit')}
                            />
                        )}
                    />
                    <AccountDetailsRow label={t('app_version')} subLabel={getAppVersion()} />
                    <SizedBox value={20} />
                    <Button
                        label={t('close_account')}
                        variant={'danger'}
                        onPress={handleOnConfirm}
                        style={styles.closeAccountButton}
                        textStyle={styles.closeAccountButtonText}
                    />
                    <SizedBox value={32} />
                </MaxWidthWrapper>
            </ScrollView>
        </Screen>
    );
};

const styles = StyleSheet.create({
    closeAccountButton: {
        backgroundColor: ds.colorValue.background.neutral.subtler,
    },
    closeAccountButtonText: {
        color: ds.colorValue.text.danger,
    },
});
